import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import fileDownload from "js-file-download";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";
import { ClipLoader } from "react-spinners";
import api from "services/api";
import { useCompanies } from "hooks/companies";
import { useToast } from "../../../../hooks/toast";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotTitleStyle,
} from "../../../../components/autopilot/styles";
import "./styles.css";

const createDefaultFilters = () => ({
  desde: moment().startOf("month").format("YYYY-MM-DD"),
  ate: moment().format("YYYY-MM-DD"),
});

const QUICK_RANGES = [
  { key: "month", label: "Mês atual", days: null },
  { key: "30d", label: "30 dias", days: 29 },
  { key: "90d", label: "90 dias", days: 89 },
  { key: "180d", label: "180 dias", days: 179 },
];

const STATUS_FILTROS = [
  { key: null, label: "Todos" },
  { key: "abaixo", label: "Abaixo do piso" },
  { key: "conforme", label: "Conforme" },
  { key: "nao_calculado", label: "Não calculado" },
  { key: "nao_aplicavel", label: "Não se aplica" },
];

const STATUS_LABEL = {
  conforme: "Conforme",
  abaixo: "Abaixo",
  nao_calculado: "Não calc.",
  nao_aplicavel: "Não se aplica",
};

const getQuickRangeDates = (rangeKey) => {
  const today = moment();
  if (rangeKey === "month") {
    return {
      desde: today.clone().startOf("month").format("YYYY-MM-DD"),
      ate: today.format("YYYY-MM-DD"),
    };
  }
  const range = QUICK_RANGES.find((item) => item.key === rangeKey);
  const days = range && range.days != null ? range.days : 29;
  return {
    desde: today.clone().subtract(days, "days").format("YYYY-MM-DD"),
    ate: today.format("YYYY-MM-DD"),
  };
};

const fmtPct = (v) =>
  v === null || v === undefined
    ? "-"
    : `${Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

const fmtInt = (v) => Number(v || 0).toLocaleString("pt-BR");

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

const fmtIndice = (v) =>
  v === null || v === undefined
    ? "-"
    : Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      });

const fmtData = (v) => (v ? moment(String(v).slice(0, 10)).format("DD/MM/YYYY") : "-");

// Cor de status por tom. Vai para a CSS var --antt-status-color, consumida via
// color-mix() no styles.css (borda, label e ícone), espelhando o padrão de
// status-card da Auditoria de Faturas.
const TONE_COLOR = {
  success: "#00bf9f",
  danger: "#fd5d93",
  warning: "#ffb236",
  info: "#1d8cf8",
};

const StatusCard = ({ label, value, helper, iconClassName, tone }) => (
  <div
    className="antt-status-card"
    style={{ "--antt-status-color": TONE_COLOR[tone] || TONE_COLOR.info }}
  >
    <div className="antt-status-card__top">
      <span className="antt-status-card__label">{label}</span>
      {iconClassName ? (
        <span className="antt-status-card__icon">
          <i className={iconClassName} />
        </span>
      ) : null}
    </div>
    <div className="antt-status-card__value">{value}</div>
    {helper ? <div className="antt-status-card__helper">{helper}</div> : null}
  </div>
);

const PisoAnttIndicador = () => {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const { addToast } = useToast();
  const empresaId = empresa && empresa.value;

  const [filtros, setFiltros] = useState(createDefaultFilters);
  const filtrosRef = useRef(filtros);
  const [data, setData] = useState(null);
  const [fretes, setFretes] = useState([]);
  const [statusFiltro, setStatusFiltro] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consultado, setConsultado] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  useEffect(() => {
    filtrosRef.current = filtros;
  }, [filtros]);

  const loadDashboard = useCallback(
    async (activeFilters) => {
      if (!empresaId || !hasEmpresaSelection) return;
      if (!activeFilters.desde || !activeFilters.ate) {
        setError('Informe o período ("De" e "Até").');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const baseParams = {
          idEmbarcador: empresaId,
          desde: activeFilters.desde,
          ate: activeFilters.ate,
        };
        const [cardsRes, fretesRes] = await Promise.all([
          api.get("/indicadores/antt/cards", { params: baseParams }),
          api.get("/indicadores/antt/drilldown", {
            params: { ...baseParams, limit: 500 },
          }),
        ]);
        setData(cardsRes.data);
        setFretes((fretesRes.data && fretesRes.data.fretes) || []);
        setConsultado(true);
      } catch (requestError) {
        const msg =
          (requestError.response &&
            requestError.response.data &&
            requestError.response.data.error) ||
          "Erro ao carregar o indicador ANTT.";
        setError(msg);
        setConsultado(true);
        addToast({
          place: "tr",
          type: "danger",
          title: "Indicador ANTT",
          description: msg,
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setLoading(false);
      }
    },
    [empresaId, hasEmpresaSelection, addToast]
  );

  useEffect(() => {
    if (!hasEmpresaSelection) {
      setData(null);
      setFretes([]);
      setError(null);
      setConsultado(false);
      return;
    }
    loadDashboard(filtrosRef.current);
  }, [empresaId, hasEmpresaSelection, loadDashboard]);

  const applyQuickRange = (key) => {
    const next = getQuickRangeDates(key);
    setFiltros(next);
    loadDashboard(next);
  };

  const handleExport = async () => {
    if (!empresaId) return;
    setExportLoading(true);
    try {
      const res = await api.get("/indicadores/antt/drilldown/export", {
        params: {
          idEmbarcador: empresaId,
          desde: filtros.desde,
          ate: filtros.ate,
          status: statusFiltro || undefined,
        },
        responseType: "blob",
      });
      fileDownload(res.data, `piso-antt-${filtros.desde}-a-${filtros.ate}.xlsx`);
    } catch (requestError) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Exportar XLSX",
        description: "Falha ao gerar a planilha.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setExportLoading(false);
    }
  };

  if (!hasEmpresaSelection) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice
          title="Selecione um embarcador no cabeçalho"
          message="Este indicador usa o embarcador selecionado no topo para montar o recorte do piso mínimo ANTT."
        />
      </div>
    );
  }

  const cards = data && data.cards;
  const hasResults = consultado && data && data.totalAvaliado > 0 && cards;
  const isEmpty = consultado && data && data.totalAvaliado === 0;
  const fretesFiltrados = statusFiltro
    ? fretes.filter((f) => f.status === statusFiltro)
    : fretes;
  const activeRange = QUICK_RANGES.find((range) => {
    const r = getQuickRangeDates(range.key);
    return r.desde === filtros.desde && r.ate === filtros.ate;
  });
  const periodoLabel =
    filtros.desde && filtros.ate
      ? `${moment(filtros.desde).format("DD/MM/YYYY")} a ${moment(
          filtros.ate
        ).format("DD/MM/YYYY")}`
      : "";

  return (
    <div className="content">
      {/* Hero: container com o fundo característico (espelha Auditoria de Faturas). */}
      <Card
        className="card-everlog-shell"
        style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}
      >
        <CardHeader
          style={{
            ...autopilotHeaderStyle,
            borderBottom: "none",
            padding: "1.65rem 1.65rem 1.45rem",
          }}
        >
          <div style={{ ...autopilotEyebrowStyle, marginBottom: "0.6rem" }}>
            Conformidade Regulatória
          </div>
          <h2
            style={{
              ...autopilotTitleStyle,
              margin: "0 0 0.55rem",
              fontSize: "2rem",
              fontWeight: 800,
              lineHeight: 1.15,
            }}
          >
            Piso Mínimo ANTT
          </h2>
          <p
            style={{
              ...autopilotDescriptionStyle,
              maxWidth: 820,
              marginBottom: 0,
              fontSize: "0.98rem",
              lineHeight: 1.65,
            }}
          >
            Compara o frete praticado (CT-e) com o piso mínimo da Resolução ANTT
            6.076/2026, por embarcador e período. Avalia carga lotação.
          </p>
        </CardHeader>
      </Card>

      {/* Painel de controle: período (campo preferencial) + Filtros + resumo. */}
      <Card
        className="card-everlog-shell"
        style={{
          ...autopilotCardStyle,
          marginTop: "1.25rem",
          background: "var(--everlog-card-hero-bg)",
        }}
      >
        <CardBody style={autopilotBodyStyle}>
          <div className="antt-busca">
            <div className="antt-busca__label">
              <span className="antt-busca__title">Período</span>
              {periodoLabel ? (
                <span className="antt-busca__hint">
                  {periodoLabel}
                  {!activeRange ? " (personalizado)" : ""}
                </span>
              ) : null}
            </div>
            <div className="antt-quick-ranges">
              {QUICK_RANGES.map((range) => (
                <button
                  key={range.key}
                  type="button"
                  className={`antt-quick-range${
                    activeRange && activeRange.key === range.key ? " is-active" : ""
                  }`}
                  disabled={loading}
                  onClick={() => applyQuickRange(range.key)}
                >
                  {range.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`antt-busca__filtros${mostrarFiltros ? " is-open" : ""}`}
              onClick={() => setMostrarFiltros((v) => !v)}
              aria-expanded={mostrarFiltros}
            >
              <i className="tim-icons icon-settings-gear-63" />
              <span>Filtros</span>
            </button>
          </div>

          {mostrarFiltros ? (
            <section className="antt-advfilters">
              <Row className="align-items-end">
                <Col lg="3" md="6">
                  <FormGroup>
                    <Label>De</Label>
                    <Input
                      type="date"
                      value={filtros.desde}
                      disabled={loading}
                      onChange={(e) => {
                        const valor = e.target.value;
                        setFiltros((current) => ({ ...current, desde: valor }));
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col lg="3" md="6">
                  <FormGroup>
                    <Label>Até</Label>
                    <Input
                      type="date"
                      value={filtros.ate}
                      disabled={loading}
                      onChange={(e) => {
                        const valor = e.target.value;
                        setFiltros((current) => ({ ...current, ate: valor }));
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col lg="6" md="12" className="antt-advfilters__actions">
                  <Button color="info" disabled={loading} onClick={() => loadDashboard(filtros)}>
                    {loading ? "Carregando..." : "Consultar"}
                  </Button>
                </Col>
              </Row>
            </section>
          ) : null}

          {hasResults ? (
            <section className="antt-status-section">
              <div className="antt-status-section__head">
                <div className="antt-status-section__title">Resumo do período</div>
                <div className="antt-status-section__subtitle">
                  Conformidade do frete praticado contra o piso mínimo da ANTT.
                </div>
              </div>
              <div className="antt-status-grid">
                <StatusCard
                  label="Conformidade"
                  tone="success"
                  iconClassName="tim-icons icon-check-2"
                  value={fmtPct(cards.conformidade.pct)}
                  helper={`${fmtInt(cards.conformidade.qtd)} de ${fmtInt(
                    data.calculados
                  )} fretes acima do piso`}
                />
                <StatusCard
                  label="Abaixo do piso"
                  tone="danger"
                  iconClassName="tim-icons icon-alert-circle-exc"
                  value={fmtInt(cards.abaixoPiso.qtd)}
                  helper={`${fmtPct(cards.abaixoPiso.pct)} dos fretes calculados`}
                />
                <StatusCard
                  label="Exposição (R$)"
                  tone="warning"
                  iconClassName="tim-icons icon-money-coins"
                  value={fmtBRL(cards.abaixoPiso.exposicaoReais)}
                  helper="Valor total abaixo do piso ANTT no período"
                />
                <StatusCard
                  label="Cobertura"
                  tone="info"
                  iconClassName="tim-icons icon-chart-bar-32"
                  value={fmtPct(cards.cobertura.pct)}
                  helper={`${fmtInt(data.calculados)} de ${fmtInt(
                    data.emEscopo != null ? data.emEscopo : data.totalAvaliado
                  )} em escopo · ${fmtPct(cards.estimado.pct)} estimado`}
                />
              </div>
              {data.naoAplicavel > 0 ? (
                <p className="antt-footnote">
                  {fmtInt(data.naoAplicavel)} CT-e(s) fora de escopo (não-lotação:
                  carga abaixo do mínimo configurado), não entram na conformidade
                  nem na exposição.
                </p>
              ) : null}
              {data.naoCalculados > 0 ? (
                <p className="antt-footnote">
                  {fmtInt(data.naoCalculados)} CT-e(s) não calculados (sem distância
                  de rota disponível), fora dos percentuais acima.
                </p>
              ) : null}
            </section>
          ) : null}
        </CardBody>
      </Card>

      {loading ? (
        <div className="antt-loading">
          <ClipLoader color={AUTOPILOT_COLORS.title} size={32} />
        </div>
      ) : error ? (
        <Card
          className="card-everlog-shell"
          style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}
        >
          <CardBody style={autopilotBodyStyle}>
            <div className="antt-state antt-state--error">{error}</div>
          </CardBody>
        </Card>
      ) : isEmpty ? (
        <Card
          className="card-everlog-shell"
          style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}
        >
          <CardBody style={autopilotBodyStyle}>
            <div className="antt-state">
              Nenhum CT-e avaliado no período. Verifique se o embarcador está
              habilitado para o indicador ANTT (carga lotação) e se há fretes a
              partir de 20/01/2026.
            </div>
          </CardBody>
        </Card>
      ) : hasResults ? (
        <Card
          className="card-everlog-shell"
          style={{
            ...autopilotCardStyle,
            marginTop: "1.25rem",
            background: "var(--everlog-card-hero-bg)",
          }}
        >
          <CardHeader style={autopilotHeaderStyle}>
            <div className="antt-drill-head">
              <div>
                <div style={autopilotEyebrowStyle}>Detalhe</div>
                <div style={autopilotTitleStyle}>Fretes avaliados</div>
              </div>
              <Button
                color="success"
                size="sm"
                disabled={exportLoading || fretes.length === 0}
                onClick={handleExport}
              >
                <i className="tim-icons icon-cloud-download-93" />{" "}
                {exportLoading ? "Gerando..." : "Exportar XLSX"}
              </Button>
            </div>
            <div className="antt-status-filtros">
              {STATUS_FILTROS.map((s) => (
                <button
                  key={s.key || "todos"}
                  type="button"
                  className={`antt-pill${statusFiltro === s.key ? " is-active" : ""}`}
                  onClick={() => setStatusFiltro(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardBody style={autopilotBodyStyle}>
            <div className="antt-table-wrap">
              <Table className="antt-table" responsive>
                <thead>
                  <tr>
                    <th>Documento</th>
                    <th>Emissão</th>
                    <th>Transportadora</th>
                    <th>Trecho</th>
                    <th className="antt-num">Frete</th>
                    <th className="antt-num">Piso ANTT</th>
                    <th className="antt-num">Índice</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {fretesFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="antt-table-empty">
                        Nenhum frete neste filtro.
                      </td>
                    </tr>
                  ) : (
                    fretesFiltrados.map((f) => (
                      <tr key={f.idCte}>
                        <td>{f.numeroCte != null ? f.numeroCte : f.idCte}</td>
                        <td>{fmtData(f.dataEmissao)}</td>
                        <td>{f.transportadora || "-"}</td>
                        <td>
                          {(f.ufOrigem || "?")}→{(f.ufDestino || "?")}
                        </td>
                        <td className="antt-num">
                          {f.valorFrete != null ? fmtBRL(f.valorFrete) : "-"}
                        </td>
                        <td className="antt-num">
                          {f.pisoAntt != null ? fmtBRL(f.pisoAntt) : "-"}
                        </td>
                        <td className="antt-num">{fmtIndice(f.indiceAntt)}</td>
                        <td>
                          <span className={`antt-badge antt-badge--${f.status}`}>
                            {STATUS_LABEL[f.status] || f.status}
                          </span>
                          {f.isEstimado ? (
                            <span className="antt-badge antt-badge--est">est.</span>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
            {fretes.length >= 500 ? (
              <p className="antt-footnote">
                Mostrando os 500 primeiros (piores índices). Use Exportar XLSX
                para a lista completa.
              </p>
            ) : null}
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
};

export default PisoAnttIndicador;
